import {
  add,
  apiDatesAdapters,
  isBefore,
  startOfDay
} from '@sequencehq/utils/dates'
import { Currency } from '@sequencehq/api'
import { AmountsValue, normaliseAmounts } from '@sequencehq/utils'
import { dequal } from 'dequal'
import {
  CubeReducerState,
  Price,
  ResolvedPhase
} from 'modules/Cube/domain/cube.domain.types'
import { getOriginalPhaseRunningOn } from 'modules/Cube/domain/postActionStages/queries/getOriginalPhaseRunningOn'
import { getPhaseAnalysis } from 'modules/Cube/domain/postActionStages/queries/getPhaseAnalysis/getPhaseAnalysis'
import { getRecurrenceDayOfMonth } from 'modules/Cube/domain/postActionStages/queries/getResolvedPhaseData'

const withDates = (
  phase: ResolvedPhase,
  absolute: ResolvedPhase['dates']['absolute']
): ResolvedPhase => ({
  ...phase,
  dates: { ...phase.dates, absolute: { ...phase.dates.absolute, ...absolute } }
})

// The schedule and the quote can write the same amount differently, e.g. "50"
// and "50.00", and the analysis compares prices and minimums as text. So the
// quote's version is taken wherever the two hold the same amounts.
const sameAmountAs = <T extends AmountsValue>(
  value: T,
  other: T | undefined
): T =>
  other !== undefined &&
  dequal(normaliseAmounts(value), normaliseAmounts(other))
    ? other
    : value

/**
 * The phase of the schedule the quote was made from that runs up to the day the
 * amendment takes effect. The schedule is locked while the amendment is live,
 * so this is the phase billing keeps up to that day - which may be a later one
 * than the quote's own phase before the amendment. Its prices take the ids,
 * and the way amounts are written, of the same products in the phase after
 * it, so the analysis matches prices, discounts and minimums across the two.
 * The snapshot has no billing day, so it bills on `billingDay` when we know
 * the schedule's, and on the phase after's otherwise.
 */
const getOriginalPhaseBefore = ({
  original,
  appliedFrom,
  phaseAfter,
  currency,
  billingDay
}: {
  original: NonNullable<
    CubeReducerState['data']['quote']['amendment']
  >['original']
  appliedFrom: Date
  phaseAfter: ResolvedPhase
  currency: Currency | undefined
  billingDay: number | undefined
}): ResolvedPhase | undefined => {
  const running = getOriginalPhaseRunningOn({
    original,
    date: add(appliedFrom, { days: -1 })
  })

  if (!running || !currency) {
    return
  }

  const priceFor = (productId: string) =>
    phaseAfter.prices.find(price => price.productId === productId)
  const priceIdFor = (productId: string) =>
    priceFor(productId)?.id ?? `original-${productId}`
  const priceIdsFor = (productIds: string[]) => productIds.map(priceIdFor)

  return {
    ...phaseAfter,
    id: 'original-schedule',
    prices: running.prices.map((price): Price => {
      const priceAfter = priceFor(price.productId)

      return {
        id: priceIdFor(price.productId),
        currency,
        integrationIds: [],
        createdAt: '',
        ...priceAfter,
        ...price,
        structure: sameAmountAs(price.structure, priceAfter?.structure)
      }
    }),
    discounts: running.discounts.map((discount, index) => ({
      id: `original-discount-${index}`,
      message: discount.message,
      discountCalculationType: discount.discountCalculationType,
      amount: parseFloat(discount.amount),
      priceIds: priceIdsFor(discount.productIds),
      // No products means the discount applies to every price.
      applyToAllPrices: discount.productIds.length === 0,
      seatDiscountType: discount.seatDiscountType
    })),
    minimums: running.minimums.map((minimum, index) => ({
      id: `original-minimum-${index}`,
      value: sameAmountAs(minimum.amount, phaseAfter.minimums[index]?.value),
      name: '',
      scope: {
        target: minimum.productIds.length ? 'specific' : 'allUsage',
        priceIds: priceIdsFor(minimum.productIds)
      }
    })),
    globalDiscount: undefined,
    recurrenceDayOfMonth: billingDay ?? phaseAfter.recurrenceDayOfMonth,
    dates: {
      duration: phaseAfter.dates.duration,
      absolute: { start: apiDatesAdapters.fromApi(running.startDate) }
    },
    isBeforeAmendmentEffectiveFrom: true,
    analysis: null
  }
}

/**
 * The phases billing has from the day before an amendment takes effect, when
 * that's later than the day it was signed for: the schedule's phase running
 * then, ending the day before, followed by the amendment's phases that aren't
 * over by then, the first starting on the day. Each is analysed again, so a
 * billing period the new start date splits shows up as a warning.
 * `originalBillingDay` is the day the schedule bills on up to then, which
 * only the live schedule knows - undefined when it couldn't be loaded.
 */
export const getPhasesAppliedFrom = ({
  orderedPhases,
  appliedFrom,
  reducerState,
  originalBillingDay
}: {
  orderedPhases: ResolvedPhase[]
  appliedFrom: Date
  reducerState: Pick<CubeReducerState, 'data'>
  originalBillingDay: number | undefined
}): ResolvedPhase[] => {
  const before = orderedPhases.filter(
    phase => phase.isBeforeAmendmentEffectiveFrom
  )
  const running = orderedPhases.filter(
    phase =>
      !phase.isBeforeAmendmentEffectiveFrom &&
      !(
        phase.dates.absolute.end &&
        isBefore(startOfDay(phase.dates.absolute.end), startOfDay(appliedFrom))
      )
  )
  const [firstRunning, ...restRunning] = running
  const original = reducerState.data.quote.amendment?.original
  const lastBefore =
    (firstRunning &&
      original &&
      getOriginalPhaseBefore({
        original,
        appliedFrom,
        phaseAfter: firstRunning,
        currency: reducerState.data.common.currency,
        billingDay: originalBillingDay
      })) ||
    before.at(-1)

  if (!lastBefore || !firstRunning) {
    return orderedPhases
  }

  const moved = [
    withDates(lastBefore, { end: add(appliedFrom, { days: -1 }) }),
    withDates(firstRunning, { start: appliedFrom }),
    ...restRunning
  ]

  const reanalysed = moved.reduce<ResolvedPhase[]>((acc, phase) => {
    const previousPhase = acc.at(-1)
    const withRecurrence = {
      ...phase,
      recurrenceDayOfMonth: getRecurrenceDayOfMonth({
        recurrencePreference: phase.recurrencePreference,
        scheduleRecurrenceDayOfMonth: phase.recurrenceDayOfMonth,
        previousPhaseRecurrence: previousPhase?.recurrenceDayOfMonth,
        phaseStartDate: phase.dates.absolute.start
      })
    }

    return [
      ...acc,
      {
        ...withRecurrence,
        analysis: getPhaseAnalysis({ reducerState, previousPhase })(
          withRecurrence
        )
      }
    ]
  }, [])

  return reanalysed
}
